import React from 'react';
import { 
  FormControl, 
  FormGroup, 
  FormControlLabel, 
  Checkbox, 
  FormHelperText, 
  Paper,
  Typography,
  Box,
  Alert
} from '@mui/material';

const CircuitSelection = ({ 
  circuits, 
  selectedCircuits, 
  onChange, 
  error 
}) => {
  // Modified useEffect to auto-select ALL circuits, not just a single one
  React.useEffect(() => {
    if (circuits && circuits.length > 0 && selectedCircuits.length !== circuits.length) {
      // Extract the IDs for all circuits
      const allCircuitIds = circuits.map(circuit => {
        return typeof circuit === 'object' ? circuit.id : circuit;
      });
      onChange(allCircuitIds);
    }
  }, [circuits, selectedCircuits.length, onChange]);

  // Convert a circuit to its ID string representation
  const getCircuitId = (circuit) => {
    if (typeof circuit === 'object' && circuit !== null) {
      return circuit.id || JSON.stringify(circuit);
    }
    return circuit;
  };

  const handleChange = (circuitId) => {
    // Function preserved for consistency, but will not actually change selection
    // since the checkboxes will appear checked but won't respond to clicks
    const currentIndex = selectedCircuits.indexOf(circuitId);
    const newSelected = [...selectedCircuits];

    if (currentIndex === -1) {
      newSelected.push(circuitId);
    } else {
      // We'll prevent deselection by not removing items
      // This ensures all circuits remain selected
      return;
    }

    onChange(newSelected);
  };

  // Handle empty or invalid circuits array
  if (!circuits || !Array.isArray(circuits) || circuits.length === 0) {
    return null; // Return nothing instead of an alert
  }

  // Process circuits to normalize data structure - extract IDs consistently
  const circuitItems = circuits.map(circuit => getCircuitId(circuit));

  return (
    <FormControl component="fieldset" error={!!error} fullWidth>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: 'rgba(0, 0, 0, 0.6)' }}>
        Available Circuits *
      </Typography>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          border: error ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)', 
          borderRadius: 1,
          '&:hover': {
            borderColor: error ? '#d32f2f' : 'rgba(0, 0, 0, 0.87)'
          }
        }}
      >
        <FormGroup>
          {circuitItems.map((circuitId) => (
            <FormControlLabel
              key={circuitId}
              control={
                <Checkbox
                  checked={selectedCircuits.includes(circuitId)}
                  onChange={() => handleChange(circuitId)}
                  // Instead of disabling (which makes it look dull), we make it read-only
                  // by having a no-op handleChange function
                  color="primary"
                />
              }
              label={
                <Box component="span" sx={{ fontWeight: 400, fontSize: '0.875rem' }}>
                  {circuitId}
                </Box>
              }
              sx={{ mb: 0.5 }}
            />
          ))}
        </FormGroup>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {!error && (
          <FormHelperText sx={{ mt: 1 }}>
            {circuitItems.length === 1 
              ? "Single circuit automatically selected" 
              : "All circuits for this site are automatically selected"}
          </FormHelperText>
        )}
      </Paper>
    </FormControl>
  );
};

export default CircuitSelection;